/**
 * Guest State Manager
 * Centralized utility to manage guest localStorage states and prevent conflicts
 * when users are logged in.
 */

/**
 * Check if user is logged in
 * @returns {boolean} True if user is logged in
 */
export const isUserLoggedIn = () => {
  const user_id = localStorage.getItem('user_id');
  return user_id && user_id !== 'null' && parseInt(user_id) > 0;
};

/**
 * Check if there's an active event booking flow in progress
 * @returns {boolean} True if event booking is in progress
 */
export const isEventBookingInProgress = () => {
  const bookingEvent = localStorage.getItem('booking_event');
  const bookingEventGuest = localStorage.getItem('booking_event_guest');
  const checkoutInProgress = localStorage.getItem('event_checkout_in_progress');
  
  return !!(bookingEvent || bookingEventGuest || checkoutInProgress === 'true');
};

/**
 * Get list of all guest-related localStorage keys
 * @param {boolean} includeEventBooking - Whether to include event booking keys
 * @returns {string[]} Array of guest localStorage keys
 */
export const getGuestStateKeys = (includeEventBooking = false) => {
  const keys = [
    'guest_cart_products',
    'guest_cart_magazines',
    // IMPORTANT: guest_cart_events is now used for machine tests by BOTH guest and logged-in users
    // It should ONLY be cleared when explicitly clearing the cart, not during login/state management
    // 'guest_cart_events', // REMOVED - machine tests use this for all users
    'guest_user_id_temp',
    'guest_billing_address',
    'guest_shipping_address',
    'guest_allowed',
    'continue_as_guest',
    'isGuestAutoCreated'
  ];
  
  if (includeEventBooking) {
    keys.push(
      'booking_event_guest',
      'guest_participants',
      'guest_event_participants',
      'selected_participant_types',
      'event_checkout_in_progress',
      'event_checkout_proceeding'
    );
  }
  
  return keys;
};

/**
 * Clear all guest states from localStorage
 * IMPORTANT: This preserves event booking states if a booking is in progress
 * @param {boolean} force - If true, clears even during event booking (use with caution)
 */
export const clearGuestStates = (force = false) => {
  const isBookingActive = isEventBookingInProgress();
  
  // If event booking is active and not forcing, only clear non-booking guest states
  const includeEventBooking = force || !isBookingActive;
  const keys = getGuestStateKeys(includeEventBooking);
  
  keys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
    }
  });
  
  return keys;
};

/**
 * Clear guest states when user logs in
 * This is safe to call after login - it preserves event booking flow
 */
export const clearGuestStatesOnLogin = () => {
  if (!isUserLoggedIn()) {
    return;
  }
  
  // Check if there's an active event booking
  const isBookingActive = isEventBookingInProgress();
  
  if (isBookingActive) {
    // Clear non-booking guest states
    const nonBookingKeys = [
      'guest_cart_products',
      'guest_cart_magazines',
      'guest_cart_events',
      'guest_allowed',
      'continue_as_guest',
      'guest_billing_address',
      'guest_shipping_address',
      'guest_user_id_temp',
      'isGuestAutoCreated'
    ];
    
    nonBookingKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
      }
    });
    
    // Promote guest booking to logged-in booking
    const guestBooking = localStorage.getItem('booking_event_guest');
    if (guestBooking) {
      localStorage.setItem('booking_event', guestBooking);
      localStorage.removeItem('booking_event_guest');
    }
    
    // DON'T remove guest_participants or guest_event_participants yet
    // They'll be used during checkout and cleared after payment
  } else {
    // No active booking - clear all guest states
    clearGuestStates(true);
  }
};

/**
 * Prevent guest state creation when user is logged in
 * Call this before any operation that would create guest states
 * @returns {boolean} True if operation should proceed, false if should be blocked
 */
export const shouldAllowGuestOperation = () => {
  if (isUserLoggedIn()) {
    return false;
  }
  return true;
};

/**
 * Safe wrapper for guest cart operations
 * Only proceeds if user is not logged in
 * @param {Function} operation - The guest cart operation to perform
 * @returns {*} Result of operation or null if blocked
 */
export const safeGuestOperation = (operation) => {
  if (!shouldAllowGuestOperation()) {
    return null;
  }
  return operation();
};
